/**
 * RIFT — shared tactical-HUD design tokens.
 *
 * Every full-screen menu panel (armory, settings, personnel — the home screen
 * itself owns its own copy, see mainmenu.js) injects `HUD_TOKENS` + `HUD_CSS`
 * alongside its own scoped stylesheet so the whole front end reads as one
 * system: same shears, same amber/cyan accents, same corner brackets, same
 * condensed/mono type. Nothing here is a standalone component — every class
 * below only ever appears nested under a screen's own `.xx-root`.
 */
import { FONT_STACK, FONT_DISPLAY, FONT_MONO } from './util.js';

/**
 * Custom properties. Paste inside a screen's own `.xx-root { ... }` block.
 *
 *   --amber  ─ primary accent (gold)
 *   --amber-2 / -3 ─ amber highlight + glow tints
 *   --red ─ critical / lock
 *   --cyan / --cyan-2 / --cyan-3 ─ secondary accent (HUD readouts, active state)
 *   --violet ─ tertiary, used sparingly for "neon trim"
 *   --ok ─ success / live indicator
 *   --ink / --ink-2 / --ink-3 ─ body / secondary / tertiary text
 *   --hair / --hair-2 ─ hairline borders
 *   --panel / --panel-2 ─ surface fills
 *   --noise-url ─ a pre-encoded SVG turbulence pattern for film-grain overlays
 */
const NOISE = `<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.92' numOctaves='2' seed='7' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 .6  0 0 0 0 .7  0 0 0 0 .85  0 0 0 .55 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>`;
const NOISE_URL = `url("data:image/svg+xml;utf8,${NOISE.replace(/\n/g, '')}")`;

export const HUD_TOKENS = `
  --amber:#ffb02a; --amber-2:#ffc251; --amber-3:#ffd884; --amber-4:#ffe9b8;
  --red:#ff3f31; --red-2:#ff7466;
  --cyan:#79d2ff; --cyan-2:#9fe1ff; --cyan-3:#c4edff;
  --violet:#b692ff;
  --ok:#a8e86a; --ok-2:#c8f08a;
  --ink:#f4f8fc; --ink-2:rgba(208,222,232,.82); --ink-3:rgba(180,196,207,.58);
  --hair:rgba(190,214,230,.22); --hair-2:rgba(190,214,230,.12);
  --panel:rgba(8,13,17,.88); --panel-2:rgba(10,16,21,.72);
  --shadow-1:0 8px 28px rgba(0,0,0,.55);
  --shadow-2:0 14px 44px rgba(0,0,0,.6);
  --shadow-amber:0 0 22px rgba(255,176,42,.28);
  --shadow-cyan:0 0 18px rgba(121,210,255,.25);
  --ff:${FONT_STACK}; --fd:${FONT_DISPLAY}; --fm:${FONT_MONO};
  --shear:polygon(0 0,100% 0,100% calc(100% - 14px),calc(100% - 14px) 100%,0 100%);
  --shear-sm:polygon(0 0,100% 0,100% calc(100% - 8px),calc(100% - 8px) 100%,0 100%);
  --shear-lg:polygon(0 0,100% 0,100% calc(100% - 22px),calc(100% - 22px) 100%,0 100%);
  --noise:${NOISE_URL};
`;

/**
 * Corner-bracket frame + surface/type primitives shared by every full-screen panel.
 *
 * Three layered backgrounds give the panels a "screen on glass" look:
 *   1. radial color washes for atmosphere
 *   2. a faint grid that fades toward the center
 *   3. a slow vertical scanline + a film-grain SVG overlay
 * The corner brackets now include a notched inner corner and a small dot
 * inset so they read as instrument-panel mounting hardware, not plain squares.
 */
export const HUD_CSS = `
.hud-corners { position:absolute; inset:0; pointer-events:none; z-index:5; }
.hud-brk { position:absolute; width:38px; height:38px;
  border:2.5px solid rgba(255,176,42,.88);
  filter: drop-shadow(0 0 6px rgba(255,176,42,.55)); }
.hud-brk::before { content:''; position:absolute; inset:3px;
  border:1px solid rgba(255,176,42,.32); }
.hud-brk::after { content:''; position:absolute; width:5px; height:5px; background:var(--amber);
  box-shadow:0 0 10px var(--amber), 0 0 18px rgba(255,176,42,.6); border-radius:1px; }
.hud-brk-tl { left:14px; top:14px; border-right:0; border-bottom:0; }
.hud-brk-tl::after { right:-2.5px; bottom:-2.5px; }
.hud-brk-tr { right:14px; top:14px; border-left:0; border-bottom:0; }
.hud-brk-tr::after { left:-2.5px; bottom:-2.5px; }
.hud-brk-bl { left:14px; bottom:14px; border-right:0; border-top:0; }
.hud-brk-bl::after { right:-2.5px; top:-2.5px; }
.hud-brk-br { right:14px; bottom:14px; border-left:0; border-top:0; }
.hud-brk-br::after { left:-2.5px; top:-2.5px; }

/* All panels paint this atmospheric layer on top of their own background. */
.hud-atmos { position:absolute; inset:0; pointer-events:none; z-index:0; opacity:.9; }
.hud-atmos::before { content:''; position:absolute; inset:0;
  background-image:
    linear-gradient(rgba(120,160,190,.22) 1px, transparent 1px),
    linear-gradient(90deg, rgba(120,160,190,.22) 1px, transparent 1px);
  background-size: 60px 60px;
  -webkit-mask-image: radial-gradient(120% 100% at 50% 35%, #000 20%, transparent 75%);
          mask-image: radial-gradient(120% 100% at 50% 35%, #000 20%, transparent 75%);
  opacity:.65; }
.hud-atmos::after { content:''; position:absolute; inset:0;
  background: repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 2px, rgba(0,0,0,.28) 2px 3px);
  mix-blend-mode: overlay; opacity:.28; animation: hud-scan 9s linear infinite; }
@keyframes hud-scan { from{ background-position: 0 0; } to{ background-position: 0 300px; } }

.hud-grain { position:absolute; inset:0; pointer-events:none; z-index:0; opacity:.08;
  background-image: var(--noise); background-size: 240px 240px; mix-blend-mode: overlay; }
.hud-vignette { position:absolute; inset:0; pointer-events:none; z-index:0;
  background: radial-gradient(120% 90% at 50% 50%, transparent 55%, rgba(0,0,0,.55) 100%); }
.hud-chromatic { position:absolute; inset:0; pointer-events:none; z-index:0; opacity:.55;
  background: linear-gradient(90deg, rgba(255,63,49,.06), transparent 8%, transparent 92%, rgba(121,210,255,.06));
  mix-blend-mode: screen; }

.hud-tag { font-family:var(--fm); font-size:9px; letter-spacing:.22em; color:var(--ink-3); }
.hud-eyebrow { font-family:var(--fm); font-size:10px; font-weight:700; letter-spacing:.32em; color:var(--amber);
  text-shadow: 0 0 12px rgba(255,176,42,.7), 0 0 24px rgba(255,176,42,.32);
  display:inline-flex; align-items:center; gap:12px; }
.hud-eyebrow::before { content:''; width:24px; height:2px; background: linear-gradient(90deg, transparent, var(--amber), var(--amber-2));
  box-shadow: 0 0 10px rgba(255,176,42,.55); }
.hud-eyebrow::after { content:''; width:6px; height:6px; background: var(--amber); border-radius:50%;
  box-shadow: 0 0 10px var(--amber), 0 0 18px rgba(255,176,42,.4);
  animation: hud-blink 2.4s ease-in-out infinite; }
@keyframes hud-blink { 0%,100% { opacity: 1; } 50% { opacity: .35; } }

.hud-title { font-family:var(--fd); font-weight:800; letter-spacing:.04em; color:#fff;
  text-shadow:0 2px 18px rgba(0,0,0,.7), 0 0 36px rgba(255,176,42,.18);
  background: linear-gradient(180deg, #fff 0%, #c8d8e6 70%, #8fa6b6 100%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 2px 6px rgba(0,0,0,.45)); }

.hud-btn { appearance:none; cursor:pointer; border:1px solid rgba(190,214,230,.3); background:linear-gradient(180deg, rgba(14,20,26,.85), rgba(6,10,14,.9));
  color:var(--ink); padding:10px 20px; font-family:var(--ff); font-weight:700; font-size:10px;
  letter-spacing:.2em; text-transform:uppercase; position:relative;
  transition:border-color .15s ease,color .15s ease,background .15s ease, transform .15s ease, box-shadow .15s ease; }
.hud-btn::before { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent, rgba(190,214,230,.4), transparent); }
.hud-btn:hover, .hud-btn:focus-visible { outline:0; color:#fff; border-color:rgba(255,176,42,.7);
  box-shadow: 0 0 18px rgba(255,176,42,.32), inset 0 0 0 1px rgba(255,176,42,.18); transform: translateY(-1px);
  background: linear-gradient(180deg, rgba(20,28,36,.92), rgba(8,13,17,.95)); }
.hud-btn.hud-on { color:#fff; border-color:rgba(255,176,42,.85);
  background:linear-gradient(180deg, rgba(255,176,42,.26), rgba(255,176,42,.08));
  box-shadow: 0 0 22px rgba(255,176,42,.4), inset 0 1px 0 rgba(255,176,42,.4), inset 0 0 0 1px rgba(255,176,42,.22); }
.hud-btn:disabled { cursor:not-allowed; opacity:.4; }
.hud-btn-primary { background:linear-gradient(180deg, var(--amber-3), var(--amber-2) 50%, var(--amber)); color:#100b02; border-color:rgba(255,232,180,.7);
  box-shadow: 0 4px 16px rgba(255,176,42,.42), inset 0 1px 0 rgba(255,255,255,.45), 0 0 26px rgba(255,176,42,.3); }
.hud-btn-primary::before { background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent); }
.hud-btn-primary:hover { background:linear-gradient(180deg, #ffe9a8, var(--amber-3) 50%, var(--amber-2)); color:#0a0600;
  box-shadow: 0 8px 24px rgba(255,176,42,.6), inset 0 1px 0 rgba(255,255,255,.6), 0 0 32px rgba(255,176,42,.4); }
.hud-btn-cyan { border-color:rgba(111,208,255,.55); color:var(--cyan); }
.hud-btn-cyan:hover, .hud-btn-cyan.hud-on { border-color:var(--cyan); color:#fff; background:linear-gradient(180deg, rgba(38,106,135,.55), rgba(38,106,135,.22));
  box-shadow: 0 0 20px rgba(121,210,255,.42), inset 0 1px 0 rgba(121,210,255,.35); }

/* Bar with sheen: a moving highlight travels along the fill. */
.hud-bar { position:relative; height:4px; background:rgba(4,7,10,.85);
  box-shadow:inset 0 0 0 1px rgba(0,0,0,.55), 0 0 0 1px var(--hair-2), inset 0 1px 0 rgba(0,0,0,.6); overflow:hidden; }
.hud-bar > i { position:absolute; left:0; top:0; bottom:0; display:block;
  background:linear-gradient(90deg,#a86510 0%,var(--amber) 50%,var(--amber-3) 100%);
  box-shadow:0 0 10px rgba(255,176,42,.6), inset 0 1px 0 rgba(255,255,255,.32); }
.hud-bar > i::after { content:''; position:absolute; inset:0;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.45) 50%, transparent 100%);
  background-size: 200% 100%;
  animation: hud-sheen 3.4s linear infinite; mix-blend-mode: overlay; }
@keyframes hud-sheen { from { background-position: -150% 0; } to { background-position: 250% 0; } }

.hud-close { appearance:none; cursor:pointer; border:1px solid rgba(190,214,230,.3); background:linear-gradient(180deg, rgba(14,20,26,.85), rgba(6,10,14,.9));
  color:var(--ink); padding:9px 18px; font-family:var(--ff); font-size:9px; font-weight:800; letter-spacing:.2em; text-transform:uppercase;
  position:relative; transition: all .15s ease; }
.hud-close::before { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent, rgba(190,214,230,.4), transparent); }
.hud-close:hover, .hud-close:focus-visible { color:#fff; outline:0;
  border-color: rgba(255,176,42,.7); box-shadow: 0 0 18px rgba(255,176,42,.4);
  background: linear-gradient(180deg, rgba(20,28,36,.92), rgba(8,13,17,.95)); }
`;

/**
 * Injects the four atmospheric layers (grid + scanlines, film grain, vignette,
 * chromatic edge) into a panel root, on top of whatever background the panel
 * itself draws. All pointer-events: none so they never intercept clicks.
 */
export function mountCorners(root) {
  const n = document.createElement('div');
  n.className = 'hud-corners';
  for (const c of ['tl', 'tr', 'bl', 'br']) {
    const b = document.createElement('div');
    b.className = 'hud-brk hud-brk-' + c;
    n.appendChild(b);
  }
  const atmos = document.createElement('div');
  atmos.className = 'hud-atmos';
  const grain = document.createElement('div');
  grain.className = 'hud-grain';
  const vignette = document.createElement('div');
  vignette.className = 'hud-vignette';
  const chroma = document.createElement('div');
  chroma.className = 'hud-chromatic';
  root.append(n, atmos, grain, vignette, chroma);
  return n;
}
